/**
 * Composer + chrome for the Upright chat.
 *
 * - `UprightComposer` is a custom replacement for assistant-ui's default
 *   composer. We roll our own because:
 *     1. Safari blocks programmatic `input.click()` on dynamically-created
 *        file inputs (the default assistant-ui approach). We use a real
 *        `<label>` wrapping a hidden `<input type="file">` instead, which
 *        works reliably in all browsers.
 *     2. We render an extra web-search toggle button that writes into the
 *        web search store in `upright-chat-shared` so the adapter can pick it up.
 * - `NewChatButton` lives here because it's part of the same chrome cluster.
 * - `AttachmentChip` is the chip shown inside the composer for each attachment.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_CHAT_PROVIDER, getCapabilities } from '@upright/common/chat/provider-capabilities';
import { Tooltip } from '@primer/react';
import {
  AttachmentPrimitive,
  ComposerPrimitive,
  useAssistantRuntime,
  useAui,
  useAuiState,
  useThread,
} from '@assistant-ui/react';
import { DeleteSteamlineCore, Earth, PencilSquare, TextFileStreamlineCore } from '@upright/ui/icons';
import { setChatInternalMode } from '../untidy/redux/actions/actions';
import { getChatInternalMode } from '../untidy/redux/selectors';
import { useIsTeam } from '../common/generic/hooks';
import {
  composerPlaceholder,
  getComposerFocusRequests,
  getPageChatContext,
  hiddenEntityContext,
  resetChatLoggingState,
  setPageChatContext,
  subscribeComposerFocusRequests,
  uprightAttachmentAdapter,
} from './upright-chat-shared';
import { useActiveChatProvider, useWebSearchToggle } from './use-chat-web-search';
import { AttachmentNoticeRow } from './attachment-notice-row';
import { clearAttachmentNotices, setAttachmentNotices } from './attachment-notices';
import {
  describeSupportedAttachmentFormats,
  logAttachmentUploadError,
  validateAttachmentFile,
} from './attachment-validation';
import { buildUploadErrorMessage } from './upload-error-message';

/**
 * New Chat button — resets the conversation.
 *
 * Two visual variants share one click handler:
 *   - `pill` (default) — full pill with icon + "New chat" label, used by
 *     the floating top-right cluster on the `/chat` route.
 *   - `icon` — borderless 28×28 icon button matching the side-panel
 *     header's other icon buttons (`.chat-side-panel-icon-button`).
 *     The label collapses to a tooltip + aria-label so the side-panel
 *     header stays a tidy single row of icon buttons.
 *
 * Rendered as a real `<a href="/chat">` so cmd / ctrl / middle-click opens
 * a fresh chat in a new tab. On a plain primary-button click we
 * `preventDefault()` and keep the existing in-place reset flow
 * (`runtime.switchToNewThread()`) so the user stays on the same tab — no
 * full page reload, no flash. Bare `<a>` (not a router `<Link>`) for the
 * same reason as the welcome suggestion pills: avoid client-side nav
 * hijacking the plain-click branch.
 *
 * Always visible — including in the welcome state, so the header keeps a
 * stable layout.
 */
export function NewChatButton({
  onNewChat,
  variant = 'pill',
  preservePageContext = false,
}: {
  onNewChat?: () => void;
  variant?: 'pill' | 'icon';
  /**
   * When `true`, capture the current page-context chip + hidden entity
   * binding *before* the reset and re-publish them afterwards. Used by
   * the side-panel header's `New chat` icon button (PLT-4625 follow-up
   * issue 2): the user is still on the same page when they click New
   * chat from inside the panel, so the chat should remain contextual to
   * that page — the chip stays, the model keeps the entity binding,
   * and the welcome screen can render contextual suggestion chips
   * (see `UprightChatWelcome`).
   *
   * Default `false` for the full `/chat` route's `New chat` pill —
   * navigating away from a contextual conversation there explicitly
   * means "I'm done with this entity", so we keep the existing wipe.
   */
  preservePageContext?: boolean;
}) {
  const runtime = useAssistantRuntime();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Only intercept plain primary-button clicks. Modifier or middle
    // clicks fall through to native browser navigation → new tab.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onNewChat?.();
    // Capture before `resetChatLoggingState` wipes both. Captured by
    // value (we don't hold a live reference) so the re-publish below
    // is idempotent against a concurrent reset.
    const preservedPageContext = preservePageContext ? getPageChatContext() : null;
    const preservedHiddenEntity = preservePageContext ? hiddenEntityContext.value : null;
    resetChatLoggingState();
    if (preservedPageContext !== null) {
      setPageChatContext(preservedPageContext);
    }
    if (preservedHiddenEntity !== null) {
      hiddenEntityContext.value = preservedHiddenEntity;
    }
    runtime.switchToNewThread();
  };

  if (variant === 'icon') {
    return (
      <Tooltip text="New chat" direction="s">
        <a href="/chat" className="chat-side-panel-icon-button" onClick={handleClick} aria-label="New chat">
          <PencilSquare width={16} height={16} />
        </a>
      </Tooltip>
    );
  }

  return (
    <a href="/chat" className="upright-new-chat-button" onClick={handleClick} title="New chat" aria-label="New chat">
      <PencilSquare width={16} height={16} />
      <span>New chat</span>
    </a>
  );
}

/** Simple attachment chip shown in the composer. */
function AttachmentChip() {
  const name = useAuiState(s => s.attachment.name);
  return (
    <AttachmentPrimitive.Root className="upright-attachment-chip">
      <span className="upright-attachment-chip-name">{name}</span>
      <AttachmentPrimitive.Remove className="upright-attachment-chip-remove" aria-label="Remove attachment">
        <DeleteSteamlineCore width={14} height={14} />
      </AttachmentPrimitive.Remove>
    </AttachmentPrimitive.Root>
  );
}

/**
 * Hover and accessible-name copy for the "Add files" control, naming the
 * formats the chat accepts (PLT-6061).
 *
 * This is where the supported-formats list stands, rather than in a permanent
 * hint line under the composer: a standing line competes with the placeholder
 * for the one piece of copy the user is actually reading, while the tooltip is
 * there at the moment of intent and costs no vertical space. It reaches
 * screen-reader users as the control's accessible name too — Primer's tooltip
 * hangs its `aria-describedby` on the `<label>`, which is not the focusable
 * element here (the visually-hidden `<input type="file">` is), so the name is
 * the only route that actually gets announced.
 *
 * Derived from the MIME allowlist through the same function the refusal message
 * uses, so the tooltip can't end up describing a different set of formats than
 * the refusal does. Computed once at module level: the allowlist is static.
 */
const ATTACH_FILES_LABEL = `Add files — ${describeSupportedAttachmentFormats()}`;

/**
 * Custom Composer with Safari-compatible file attach button.
 *
 * Layout (PLT-4605) — driven by a CSS grid on `.aui-composer-root` so the
 * same JSX serves both states. Two visual modes via `--landing` /
 * `--compact` modifier classes:
 *
 *   Landing (no messages yet — OR compact mode "expanded" because the user
 *   has typed multi-line content / attached a file):
 *     ┌──────────────────────────────────────────────────┐
 *     │  <textarea — placeholder>                        │
 *     │                                                  │
 *     │  [🌐 Web search] [📄 Add files] [📎 file.pdf×]  [↑]
 *     └──────────────────────────────────────────────────┘
 *     Vertical layout — taller input, pills + attachment chips on the
 *     same bottom row on the left (chips inline next to "Add files",
 *     since that's the action that produced them), send on the right.
 *
 *   Compact (active chat, single-line input, no attachments):
 *     ┌────────────────────────────────────────────────────┐
 *     │  [🌐] [📄]  <textarea — single line>          [↑] │
 *     └────────────────────────────────────────────────────┘
 *     Horizontal layout — pills move to the left of the input on a
 *     single row, pill labels collapse to icon-only, send button shrinks.
 *     The whole composer is much shorter so it doesn't compete with the
 *     conversation.
 *
 * Mode-switch policy: the layout adapts back to the landing template
 * whenever the user has either (a) attached one or more files, or
 * (b) typed text containing a newline. Both are signals that the message
 * is non-trivial and benefits from the larger surface — and in particular,
 * keeping attachment chips inline with "Add files" only reads well when
 * there's a real horizontal pill row to host them. The single-line
 * compact layout would otherwise bunch chips, icons, and input on the
 * same line.
 *
 * The same JSX is used in both modes — labels are hidden with
 * `display: none`, not unmounted, so there's no DOM thrash on the landing
 * → compact transition.
 */
export function UprightComposer() {
  const aui = useAui();
  const runtime = useAssistantRuntime();
  const hasMessages = useThread(state => state.messages.length > 0);
  const isRunning = useThread(state => state.isRunning);
  // Watch the live composer state so we can flip back to the "landing"
  // (taller, vertical) layout when the message gets non-trivial. Two
  // triggers: (1) any attachment is present — chips need horizontal room
  // inline next to "Add files"; (2) typed text contains a newline —
  // multi-line input needs the taller surface.
  //
  // Selectors return a boolean / number directly instead of the full
  // `composer.text` string, so `useAuiState`'s shallow-equal check bails
  // out of re-renders on every keystroke (we only care about the newline
  // crossing, not the character count).
  const hasAttachments = useAuiState(s => s.composer.attachments.length > 0);
  const isMultiLineText = useAuiState(s => s.composer.text.includes('\n'));
  const isExpanded = !hasMessages || hasAttachments || isMultiLineText;
  const fileInputRef = useRef<HTMLInputElement>(null);
  // PLT-6766: `startNewChat` asks for the cursor. Found through the wrapper's own DOM rather
  // than a ref on `ComposerPrimitive.Input`, so the focus does not depend on which element
  // assistant-ui forwards a ref to.
  //
  // Through a store and an effect rather than a plain subscription, so the focus lands after
  // the commit the new chat starts: a composer focused synchronously could be one that commit
  // unmounts, taking the cursor with it.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const focusRequests = useSyncExternalStore(
    subscribeComposerFocusRequests,
    getComposerFocusRequests,
    getComposerFocusRequests,
  );
  // A composer mounting now already takes focus from `autoFocus` on the input below, so only a
  // request arriving *after* this mount has anything to do.
  const handledFocusRequestsRef = useRef(focusRequests);
  useEffect(() => {
    if (handledFocusRequestsRef.current === focusRequests) return;
    handledFocusRequestsRef.current = focusRequests;
    wrapperRef.current?.querySelector('textarea')?.focus();
  }, [focusRequests]);
  // PLT-5034: which features the active provider can actually serve.
  // Capabilities come from the shared `@upright/common` table — the SAME table
  // the backend enforces — so the UI never advertises an affordance the server
  // would refuse.
  const capabilities = getCapabilities(useActiveChatProvider() ?? DEFAULT_CHAT_PROVIDER);
  // The choice lives in a store in `upright-chat-shared`, kept per provider: on
  // by default for the default provider, off at the start of every chat in EU
  // chats (PLT-7271). The adapter reads the store at send time.
  const webSearch = useWebSearchToggle();
  // Unique ID per instance to avoid duplicate-id warnings when assistant-ui
  // renders multiple composers (e.g. inline edit + bottom).
  const [fileInputId] = useState(() => `upright-file-input-${Math.random().toString(36).slice(2, 8)}`);

  /**
   * Attach a batch of files, and say so when one of them can't be attached
   * (PLT-6061).
   *
   * Three things this does that the previous `console.error` did not:
   *
   *   1. **Checks the file first.** assistant-ui refuses a file that doesn't
   *      match the adapter's `accept` string by throwing *before* the adapter's
   *      `add()` runs, with a message that lists nine MIME types
   *      (`base-composer-runtime-core`). Validating up front means the user
   *      gets our sentence, naming the formats that do work, instead.
   *   2. **Is per file.** The `try` used to wrap the whole loop, so the first
   *      bad file abandoned every file after it — pick a `.zip` and a `.pdf`
   *      together and the `.pdf` vanished too, with nothing on screen.
   *   3. **Reports.** Refusals are published to the notice store and rendered
   *      under the composer by `AttachmentNoticeRow`.
   *
   * Published once at the end rather than per file, so a multi-file pick draws
   * its whole outcome in one step instead of the row growing as the loop awaits.
   */
  const addFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      // Clear first: the previous batch's refusals are stale the moment the
      // user picks again, and leaving them up while this batch awaits would
      // read as a result of the new pick.
      clearAttachmentNotices();
      const notices: string[] = [];
      for (const file of files) {
        const rejection = validateAttachmentFile(file);
        if (rejection !== undefined) {
          // The user's sentence names the file and the formats that work, not
          // the raw MIME type; the console keeps the MIME type so a support
          // thread can still say why this file was refused (PLT-6061).
          logAttachmentUploadError('composer', rejection);
          notices.push(buildUploadErrorMessage(rejection));
          continue;
        }
        try {
          await aui.composer().addAttachment(file);
        } catch (err) {
          // Anything left is a read failure or an assistant-ui internal error,
          // so the user-facing line stays generic — the raw Error text here is
          // developer copy ("Failed to read file") and is kept to the console.
          console.error('Failed to add attachment:', err);
          notices.push(`${buildUploadErrorMessage({ filename: file.name })} Try again, or use a different file.`);
        }
      }
      setAttachmentNotices(notices);
    },
    [aui],
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      await addFiles(Array.from(files));
      // Reset the input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [addFiles],
  );

  /**
   * Paste, handled here rather than by assistant-ui (PLT-6061).
   *
   * `ComposerPrimitive.Input` attaches its own paste handler when
   * `addAttachmentOnPaste` is on (its default), and that handler ends in
   * `console.error` — so pasting an unsupported image did nothing at all, with
   * no message. It also uses `Promise.all`, so one bad file in a multi-file
   * paste dropped the rest. Taking the path over routes paste through the same
   * `addFiles` as the picker, which is the only way the two can't drift.
   *
   * Only files are intercepted; a text paste falls through untouched (no
   * `preventDefault`), including a paste that carries both.
   */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!capabilities.attachments) return;
      const files = Array.from(e.clipboardData.files);
      if (files.length === 0) return;
      e.preventDefault();
      void addFiles(files);
    },
    [addFiles, capabilities.attachments],
  );

  // A refusal is about the message being composed, so it stops being relevant
  // once that message is on its way.
  useEffect(() => {
    if (isRunning) clearAttachmentNotices();
  }, [isRunning]);

  const handleCancel = useCallback(() => {
    // Defence in depth — call both the composer's cancel (which
    // proxies to `handleCancel` → `runtime.cancelRun` on the thread
    // composer) AND the thread runtime's `cancelRun` directly. The
    // composer path can be the *edit* composer in some assistant-ui
    // states (e.g. when a message is being edited, the thread
    // composer isn't the active one), in which case its `cancel()`
    // just exits edit mode instead of aborting the in-flight run.
    // Calling `runtime.thread.cancelRun()` unconditionally guarantees
    // the abort signal fires for the adapter generator regardless of
    // which composer `aui.composer()` resolved to. PLT-4625 follow-up
    // issue 4 — "clicking stop doesn't stop the thinking".
    aui.composer().cancel();
    runtime.thread.cancelRun();
  }, [aui, runtime]);

  const stateModifier = isExpanded ? 'aui-composer-root--landing' : 'aui-composer-root--compact';

  // Tooltips render in BOTH layouts (PLT-6061).
  //
  // This used to return the bare node when expanded, on the reasoning that a
  // tooltip repeating a visible label is noise. The effect, though, was that
  // the copy existed in one layout and not the other — the same control
  // described itself in compact mode and said nothing in landing mode — which
  // is a bug rather than a design choice, and it got worse once the attach
  // tooltip became the standing home for the supported-formats list (see
  // `ATTACH_FILES_LABEL`). Wrapping unconditionally is what gives that copy a
  // home at the moment of intent, at no cost in vertical space: a permanent
  // hint line under the composer would compete with the placeholder.
  //
  // Primer's `Tooltip` renders a fragment (the cloned child plus a `[popover]`
  // span that is `display: none` until open), not a wrapper element, so the
  // pills stay direct flex children of `.upright-composer-pills` in both
  // layouts and nothing about the pill row's alignment or wrapping changes.
  const wrapWithTooltip = (text: string, node: React.ReactElement) => (
    <Tooltip text={text} direction="n">
      {node}
    </Tooltip>
  );

  const webSearchButton = (
    <button
      type="button"
      className={`upright-action-pill upright-web-search-pill ${
        webSearch.enabled ? 'upright-web-search-pill--active' : ''
      }`}
      aria-pressed={webSearch.enabled}
      aria-label="Web search"
      onClick={webSearch.toggle}
    >
      {/*
       * The icon is wrapped in a positioned anchor so the active-state
       * checkmark badge (rendered as `::after` in CSS) can pin to its
       * bottom-right corner. The badge is the persistent "selected"
       * affordance — visible in both labeled (landing) and icon-only
       * (compact) modes.
       */}
      <span className="upright-action-pill-icon">
        <Earth width={18} height={18} />
      </span>
      <span className="upright-action-pill-label">Web search</span>
    </button>
  );

  const attachLabel = (
    <label className="upright-action-pill upright-attach-pill" htmlFor={fileInputId} aria-label={ATTACH_FILES_LABEL}>
      <input
        id={fileInputId}
        ref={fileInputRef}
        type="file"
        className="upright-attach-input-hidden"
        accept={uprightAttachmentAdapter.accept}
        multiple
        onChange={e => {
          void handleFileChange(e);
        }}
      />
      <span className="upright-action-pill-icon">
        {/* TextFileStreamlineCore — explicit Streamline core-line set
           variant (page with corner fold + 3 text lines), 1.5 stroke
           so it visually matches the Earth globe icon next to it. */}
        <TextFileStreamlineCore width={18} height={18} />
      </span>
      <span className="upright-action-pill-label">Add files</span>
    </label>
  );

  return (
    <div className="upright-composer-wrapper" ref={wrapperRef}>
      {/*
       * PLT-4919: soft fade behind the composer + internal-mode row, painted
       * via a CSS gradient (transparent at top, chat-bg at bottom). The
       * composer pill is opaque but its rounded corners + the transparent
       * `InternalModeRow` below it would otherwise let scrolling chat
       * content (e.g. the value-chain visualization's long arrows) show
       * through as it scrolls under the sticky footer.
       *
       * `pointer-events: none` in CSS keeps the overlay click-through.
       * Rendered as a real DOM node (rather than a `::before` pseudo) so
       * the stacking-context ordering with the composer pill and internal-
       * mode row is straightforward — see `.upright-composer-fade-overlay`
       * in `upright-chat-theme.css`.
       */}
      <div className="upright-composer-fade-overlay" aria-hidden="true" />
      <ComposerPrimitive.Root className={`aui-composer-root ${stateModifier}`}>
        <ComposerPrimitive.Input
          autoFocus
          name="message"
          rows={1}
          className="aui-composer-input"
          placeholder={composerPlaceholder(hasMessages)}
          // PLT-6061: assistant-ui's own paste-to-attach swallows every failure
          // into `console.error`. Off, so `handlePaste` owns the path and a
          // refused paste is reported like a refused pick.
          addAttachmentOnPaste={false}
          onPaste={handlePaste}
        />
        <div className="upright-composer-pills">
          {/* PLT-5034 / PLT-5430: each pill shows only when the active provider
              supports it, per the shared capability table. Attachments work on
              both providers (the EU path sends them inline rather than via the
              US Files API), so the "Add files" pill gates off `attachments`,
              not `fileUpload`. The web search pill is also hidden while a
              customer's provider is still loading. */}
          {webSearch.available && wrapWithTooltip(webSearch.tooltip, webSearchButton)}
          {capabilities.attachments && wrapWithTooltip(ATTACH_FILES_LABEL, attachLabel)}
          {/*
           * Attachment chips render inline at the end of the pill cluster,
           * directly after the "Add files" pill — visually anchoring each
           * chip to the action that produced it. In compact mode (no
           * attachments by definition, since attachments force expanded
           * mode) this slot is empty. The chips wrap below on a second
           * line when the row gets crowded; the pill cluster's
           * `flex-wrap: wrap` handles that for us.
           */}
          <ComposerPrimitive.Attachments components={{ Attachment: AttachmentChip }} />
        </div>
        {isRunning ? (
          <button type="button" className="aui-composer-cancel" onClick={handleCancel} title="Stop" />
        ) : (
          <ComposerPrimitive.Send className="aui-composer-send" />
        )}
      </ComposerPrimitive.Root>
      {/* Refused attachments, under the composer — the design system's
          field-error position (`design/50-components.md`, Forms), since the
          composer is the product's prompt field. PLT-6061. */}
      <AttachmentNoticeRow />
      <InternalModeRow />
    </div>
  );
}

/**
 * Tiny switch row rendered directly below the composer (PLT-4475).
 *
 * Visible only to team users. Toggles the chat internal-mode debug panel
 * via the Redux `chatInternalMode` flag, persisted in localStorage. The
 * row is intentionally muted so it doesn't compete with the composer
 * controls — it's a power-user affordance, not a primary action.
 *
 * NOTE: We re-check `isTeamUser` here rather than just relying on the
 * Redux flag. A non-team user with a stale `UPRIGHT/CHAT_INTERNAL_MODE`
 * flag in their localStorage (e.g. they previously had team access) must
 * never see this row or the panel — defence in depth is cheap here.
 */
function InternalModeRow() {
  const isTeam = useIsTeam();
  const enabled = useSelector(getChatInternalMode);
  const dispatch = useDispatch();

  // Hard-gate the render on team status. The Redux flag alone isn't enough —
  // see the JSDoc above.
  if (!isTeam) return null;

  return (
    <div className="upright-chat-internal-mode-row">
      <label className="upright-chat-internal-mode-label">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => {
            dispatch(setChatInternalMode(e.target.checked));
          }}
        />
        <span className="upright-chat-internal-mode-text">Internal mode</span>
        <span className="upright-chat-internal-mode-hint">Show debug info — Upright team only</span>
      </label>
    </div>
  );
}
